/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _3 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>3 - Перебирающие методы массива</h2>
    <h2 className={styles.h2}>3 - Перебирающие методы массива</h2>
    <p className={styles.description}>
      В JavaScript есть методы массивов пришедшие из функциональных языков. Они получают исходный массив, создают новый
      пустой массив и заполняют его, применяя к копии значения каждого элемента указанную callback-функцию.{' '}
    </p>
    <p className={styles.description}>
      Все функциональные методы массивов имеют схожий синтаксис. Исходный массив{' '}
      <code className={styles.code}>array</code>, вызов метода <code className={styles.code}>method</code> и
      callback-функция <code className={styles.code}>callback</code> в качестве аргумента метода.{' '}
    </p>
    <CopyBlock
      text={`array.method(callback[currentValue, index, array])`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      В большинстве методов, аргументами callback-функции являются значение элемента{' '}
      <code className={styles.code}>currentValue</code> (первый параметр), позиция элемента{' '}
      <code className={styles.code}>index</code> (второй параметр) и сам исходный массив{' '}
      <code className={styles.code}>array</code> (третий параметр).{' '}
    </p>
    <CopyBlock
      text={`array.method((item, idx, arr) => {
  // логика которая будет применяться на каждой итерации
});`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      Все параметры кроме значения элемента <code className={styles.code}>item</code> необязательны. Названия параметров
      могут быть любые, но есть неофициальные соглашения.{' '}
    </p>
    <CopyBlock
      text={`array.method(item => {
  // логика которая будет применяться на каждой итерации
});`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.1 - Array.prototype.forEach()</h3>
      <p className={styles.description}>
        Единственный из функциональных методов который ничего не возвращает, а просто перебирает коллекцию. Используется
        как замена цикла <code className={styles.code}>for</code>.{' '}
      </p>
      <CopyBlock
        text={`array.forEach(callback[currentValue, index, array])`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <ul className={styles.list}>
        <li className={styles.list__item}>Перебирает</li>
        <li className={styles.list__item}>Может изменять</li>
        <li className={styles.list__item}>Не возвращает</li>
      </ul>
      <CopyBlock
        text={`const numbers = [1, 2, 3];

// Классический for
for (let i = 0; i < numbers.length; i += 1) {
  console.log(numbers[i]);
}

// Функциональный forEach
numbers.forEach(num => console.log(num));

// Указываем параметр idx если нужен доступ к счетчику
numbers.forEach((num, idx) => console.log(\`index $ {idx}, value $ {num}\`));`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Код более декларативный, читабельный, поддерживаемый. Поэтому дальнейшее использование цикла{' '}
        <code className={styles.code}>for</code> при выполнении домашних работ не рекомендуется. Единственным местом где
        все еще необходимо использовать цикл <code className={styles.code}>for</code>, это задача с прерыванием
        выполнения цикла, <code className={styles.code}>forEach</code> прервать инструкцией{' '}
        <code className={styles.code}>break</code> нельзя.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/forEach"
            target="_blank"
            rel="noopener noreferrer"
          >
            Array.forEach на MDN
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://thejsguy.com/2016/07/30/javascript-for-loop-vs-array-foreach.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            The for Loop vs. forEach in JavaScript
          </a>
        </li>
      </ul>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.2 - Array.prototype.map()</h3>
      <p className={styles.description}>
        Используется для транформации массива. Применяет callback-функцию к каждому элементу исходного массива,
        результат работы callback-функции записывает в новый массив, который и будет результатом выполнения метода.
        Исходный и новый массив всегда имеют одинаковую длину. Не мутирует иходный массив.{' '}
      </p>
      <CopyBlock
        text={`array.map(callback[currentValue, index, array])`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <ul className={styles.list}>
        <li className={styles.list__item}>Используется для трансформации массива</li>
        <li className={styles.list__item}>Перебирает</li>
        <li className={styles.list__item}>Не изменяет</li>
        <li className={styles.list__item}>Возвращает коллекцию</li>
      </ul>
      <CopyBlock
        text={`const numbers = [1, 2, 3];

const doubledNumbers = numbers.map(num => num * 2);
console.log(doubledNumbers); // [2, 4, 6]`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Используем map чтобы пройти по базе данных users и получить массив всех имен пользователей.{' '}
      </p>
      <CopyBlock
        text={`const users = [
  { name: 'Mango', isActive: true },
  { name: 'Poly', isActive: false },
  { name: 'Ajax', isActive: true },
];

// Для каждого элемента коллекции (user) вернем значение поля name
const names = users.map(user => user.name);

console.log(names); // ["Mango", "Poly", "Ajax"]`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        <a
          className={styles.link}
          href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/map"
          target="_blank"
          rel="noopener noreferrer"
        >
          Array.map на MDN
        </a>
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.3 - Array.prototype.filter()</h3>
      <p className={styles.description}>
        Применяет callback-функцию к каждому элементу исходного массива и если результат ее выполнения имеет значение{' '}
        <code className={styles.code}>true</code>, то копирует значение из исходного массива в новый массив. Исходный и
        новый массив могут иметь разную длину. Не мутирует иходный массив. Всегда возвращает массив, даже если в нем
        всего 1 элемент. Если ничего не найдено, вернет пустой массив. Используется когда необходимо найти более одного
        элемента в коллекции.{' '}
      </p>
      <CopyBlock
        text={`array.filter(callback[currentValue, index, array])`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <ul className={styles.list}>
        <li className={styles.list__item}>Используется для фильтрации массива</li>
        <li className={styles.list__item}>Перебирает</li>
        <li className={styles.list__item}>Не изменяет</li>
        <li className={styles.list__item}>Возвращает коллекцию</li>
      </ul>
      <CopyBlock
        text={`const numbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

console.log(numbers.filter(num => num > 5)); // [6, 7, 8, 9]

console.log(numbers.filter(num => num < 5)); // [0, 1, 2, 3, 4]

console.log(numbers.filter(num => num === 5)); // [5]`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Используем <code className={styles.code}>filter</code> чтобы пройти по базе данных{' '}
        <code className={styles.code}>users</code> и выбрать активных и неактивных пользователей по значению свойства{' '}
        <code className={styles.code}>isActive</code>.
      </p>
      <CopyBlock
        text={`const users = [
  { name: 'Mango', isActive: true },
  { name: 'Poly', isActive: false },
  { name: 'Ajax', isActive: true },
  { name: 'Chelsey', isActive: false },
];

// Для каждого элемента коллекции (user) проверим поле isActive.
// Если оно true, то текущий элемент (user) будет записан в результирующий массив.
const activeUsers = users.filter(user => user.isActive);
console.log(activeUsers);

// Для каждого элемента коллекции (user) проверим поле isActive.
// Если оно false, то текущий элемент (user) будет записан в результирующий массив.
const inactiveUsers = users.filter(user => !user.isActive);
console.log(inactiveUsers);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        <a
          className={styles.link}
          href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/filter"
          target="_blank"
          rel="noopener noreferrer"
        >
          Array.filter на MDN
        </a>
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.4 - Array.prototype.find()</h3>
      <p className={styles.description}>
        Что делать если нам нужно найти уникальный <code className={styles.code}>id</code> пользователя среди 30000
        обьектов, а среди 300000?{' '}
      </p>
      <p className={styles.description}>
        Первое что вы хотели бы сделать, это вызвать <code className={styles.code}>filter</code>, пройти по коллекции и
        вернуть только того пользователя, <code className={styles.code}>id</code> которого совпадет. Это работает, но
        какой ценой? Представьте что нужный нам объект пользователя стоит под индексом 2 в массиве. Какой смысл
        фильтровать все остальное? Мы теряем производительность из-за лишних операций.{' '}
      </p>
      <p className={styles.description}>
        Для таких случаев есть метод <code className={styles.code}>find</code>, который будет искать до первого
        совпадения, после чего прервет свое выполнение.{' '}
      </p>
      <p className={styles.description}>
        Применяет callback-функцию к каждому элементу исходного массива и если результат ее выполнения имеет значение{' '}
        <code className={styles.code}>true</code>, то возвращает этот элемент и завершает свое выполнение. Если ничего
        не найдено, вернет <code className={styles.code}>undefined</code>.{' '}
      </p>
      <CopyBlock
        text={`array.find(callback[currentValue, index, array])`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <ul className={styles.list}>
        <li className={styles.list__item}>Используется для поиска уникального элемента массива</li>
        <li className={styles.list__item}>Перебирает</li>
        <li className={styles.list__item}>Не изменяет</li>
        <li className={styles.list__item}>Возвращает элемент</li>
      </ul>
      <CopyBlock
        text={`const numbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

console.log(numbers.find(num => num > 5)); // 6

console.log(numbers.find(num => num < 5)); // 0

console.log(numbers.find(num => num === 5)); // 5`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Используем <code className={styles.code}>find</code> чтобы пройти по базе данных{' '}
        <code className={styles.code}>users</code> и найти пользователя по идентификатору{' '}
        <code className={styles.code}>id</code>. Идентификаторы всегда уникальны.{' '}
      </p>
      <CopyBlock
        text={`const users = [
  { id: '000', name: 'Mango', isActive: true },
  { id: '001', name: 'Poly', isActive: false },
  { id: '002', name: 'Ajax', isActive: true },
  { id: '003', name: 'Chelsey', isActive: false },
];

// Для каждого элемента коллекции (user) проверим поле id.
// Если оно совпадает с искомым идентификатором, то find прекратит
// выполнение и вернет текущий элемент (user) как результат выполнения
const user = users.find(user => user.id === '002');
console.log(user);

// Создадим функцию которая будет возвращать нам пользователя по id
const getUserById = (arr, id) => arr.find(x => x.id === id);

console.log(getUserById(users, '001'));
console.log(getUserById(users, '003'));`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        <a
          className={styles.link}
          href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/find"
          target="_blank"
          rel="noopener noreferrer"
        >
          Array.find на MDN
        </a>
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.5 - Array.prototype.every() и Array.prototype.some()</h3>
      <p className={styles.description}>
        Метод <code className={styles.code}>every</code> проверяет, прошли ли все элементы массива тест, предоставляемый
        callback-функцией. Возвращает <code className={styles.code}>true</code>, если вызов callback-функции вернет{' '}
        <code className={styles.code}>true</code> для каждого элемента в <code className={styles.code}>array</code>.{' '}
      </p>
      <CopyBlock
        text={`array.every(callback[currentValue, index, array])`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Метод <code className={styles.code}>some</code> проверяет, проходит ли по крайней мере один элемент в массиве
        тест, предоставляемый callback-функцией. Возвращает <code className={styles.code}>true</code>, если вызов
        callback-функции вернет <code className={styles.code}>true</code> хотябы для одного элемента в{' '}
        <code className={styles.code}>array</code>.{' '}
      </p>
      <CopyBlock
        text={`array.some(callback[(currentValue, index, array)]);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <ul className={styles.list}>
        <li className={styles.list__item}>Перебирает</li>
        <li className={styles.list__item}>Не изменяет</li>
        <li className={styles.list__item}>Возвращает true или false</li>
      </ul>
      <CopyBlock
        text={`// Функция которая проверяет величину значения, возвращает true или false.
const isBigEnough = val => val >= 10;

// Допустим нам нужно узнать достаточно ли большие ВСЕ числа в коллекции.
// Все что нам нужно это буль true/false. Метод every вернет true только тогда,
// когда все элементы коллекции будут удовлетворять условию в callback-функции.

console.log([12, 5, 8, 130, 44].every(isBigEnough)); // false
console.log([12, 54, 18, 130, 44].every(isBigEnough)); // true

// Допустим нам нужно узнать ЕСТЬ ЛИ в коллекции числа больше 10, хотя бы одно
// или больше. Все что нам нужно это буль true/false. Метод some вернет true
// только тогда, когда хотябы 1 или более элементов коллекции будут
// удовлетворять условию в callback-функции.

console.log([2, 5, 8, 1, 4].some(isBigEnough)); // false
console.log([12, 5, 8, 1, 4].some(isBigEnough)); // true`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Давайте еще пример. Есть массив объектов фруктов, необходимо узнать все ли фрукты есть в наличии и есть ли в
        наличии хоть какие-то фрукты более 0 штук.{' '}
      </p>
      <CopyBlock
        text={`const fruits = [
  { name: 'apples', amount: 100 },
  { name: 'bananas', amount: 0 },
  { name: 'grapes', amount: 50 },
];

// every вернет true только если всех фруктов будет больше чем 0 штук
const allAvailable = fruits.every(fruit => fruit.amount > 0); // false

// some вернет true если хотябы одного фрукта будет больше чем 0 штук
const anyAvailable = fruits.some(fruits => fruits.amount > 0); // true`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/every"
            target="_blank"
            rel="noopener noreferrer"
          >
            Array.every на MDN
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/some"
            target="_blank"
            rel="noopener noreferrer"
          >
            Array.some на MDN
          </a>
        </li>
      </ul>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.6 - Array.prototype.reduce()</h3>
      <p className={styles.description}>
        Аккумулирующая функция, используется для последовательной обработки каждого элемента массива с сохранением
        промежуточного результата. Швейцарский нож функциональных методов массива. Возможно немного сложна в усвоении,
        но результат стоит того.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>Перебирает</li>
        <li className={styles.list__item}>Не изменяет</li>
        <li className={styles.list__item}>Возвращает что угодно</li>
      </ul>
      <CopyBlock
        text={`array.reduce(сallback[(previousValue, currentItem, index, array)], initialValue)`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>previousValue</code> — промежуточный результат (аккумулятор)
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>currentItem</code> — текущий элемент массива
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>index</code> — индекс текущего элемента массива
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>array</code> — оригинальный массив
        </li>
      </ul>
      <p className={styles.description}>
        Легко представить ее работу на примере подсчета суммы значений массива. Если в качестве callback-функции задать
        сложение, то reduce вернет сумму всех значений массива.{' '}
      </p>
      <p className={styles.description}>Работает это так: </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Функция <code className={styles.code}>reduce</code> создаст новую переменную, называемую аккумулятор. Затем
          присвоит аккумулятору значение текущего (первого) элемента массива{' '}
          <code className={styles.code}>currentItem</code>.
        </li>
        <li className={styles.list__item}>
          Функция проверит есть ли у массива следующий элемент и если он есть, то добавит значение к аккумулятору. Когда
          следующего элемента не окажется (конец массива), функция вернет значения аккумулятора.
        </li>
        <li className={styles.list__item}>
          Вторым аргументом <code className={styles.code}>reduce</code> может быть начальное значение{' '}
          <code className={styles.code}>initialValue</code>. Это значение будет присвоено аккумулятору до применения
          callback-функции к первому элементу массива.
        </li>
      </ul>
      <CopyBlock
        text={`const numbers = [1, 2, 3, 4, 5];

const summ = numbers.reduce((acc, value) => acc + value, 0);

console.log(summ); // 15`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Допустим у нас есть следующая задача: из массива постов твиттера отдельного пользователя необходимо посчитать
        сумму всех лайков. Можно перебрать циклом <code className={styles.code}>for</code> или{' '}
        <code className={styles.code}>forEach</code>, каждое из этих решений потребует дополнительного кода. А можно
        использовать <code className={styles.code}>reduce</code>.{' '}
      </p>
      <CopyBlock
        text={`const tweets = [
  { id: '000', likes: 5, tags: ['js', 'nodejs'] },
  { id: '001', likes: 2, tags: ['html', 'css'] },
  { id: '002', likes: 17, tags: ['html', 'js', 'nodejs'] },
  { id: '003', likes: 8, tags: ['css', 'react'] },
  { id: '004', likes: 0, tags: ['js', 'nodejs', 'react'] },
];

// Пройдем по всем элементам коллекции и прибавим значения свойства likes
// к аккумулятору, начальное значаение которого укажем 0.
const likes = tweets.reduce((totalLikes, tweet) => totalLikes + tweet.likes, 0);

console.log(likes); // 32

// Наверное подсчет лайков не одиночная операция, поэтому напишем функцию
// для подсчета лайков из коллекции
const countLikes = tweets =>
  tweets.reduce((totalLikes, tweet) => totalLikes + tweet.likes, 0);

console.log(countLikes(tweets)); // 32`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Заметили свойство <code className={styles.code}>tags</code> у каждого поста? Продолжая тему{' '}
        <code className={styles.code}>reduce</code> мы соберем в массив все теги которые встречаются в постах.
      </p>
      <CopyBlock
        text={`const tweets = [
  { id: '000', likes: 5, tags: ['js', 'nodejs'] },
  { id: '001', likes: 2, tags: ['html', 'css'] },
  { id: '002', likes: 17, tags: ['html', 'js', 'nodejs'] },
  { id: '003', likes: 8, tags: ['css', 'react'] },
  { id: '004', likes: 0, tags: ['js', 'nodejs', 'react'] },
];

// Пройдем по всем элементам коллекции и добавим значения свойства tags
// к аккумулятору, начальное значение которого укажем пустым массивом [].
// На каждой итерации пушим в аккумулятор все элементы tweet.tags и возвращаем его.
const tags = tweets.reduce((allTags, tweet) => {
  allTags.push(...tweet.tags);

  return allTags;
}, []);

console.log(tags);

// Наверное сбор тегов не одиночная операция, поэтому напишем функцию
// для сбора тегов из коллекции
const getTags = tweets =>
  tweets.reduce((allTags, tweet) => {
    allTags.push(...tweet.tags);

    return allTags;
  }, []);

console.log(getTags(tweets));`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        После того как мы собрали все теги из постов, хорошо бы было посчитать количество уникальных тегов в массиве. И
        снова <code className={styles.code}>reduce</code> тут как тут.{' '}
      </p>
      <CopyBlock
        text={`const tweets = [
  { id: '000', likes: 5, tags: ['js', 'nodejs'] },
  { id: '001', likes: 2, tags: ['html', 'css'] },
  { id: '002', likes: 17, tags: ['html', 'js', 'nodejs'] },
  { id: '003', likes: 8, tags: ['css', 'react'] },
  { id: '004', likes: 0, tags: ['js', 'nodejs', 'react'] },
];

const getTags = tweets =>
  tweets.reduce((allTags, tweet) => {
    allTags.push(...tweet.tags);

    return allTags;
  }, []);

const tags = getTags(tweets);

// Вынесем callback-функцию отдельно, а в reducе передадим ссылку на нее.
// Это стандартная практика если callback-функция довольно большая.

// Если в объекте-аккумуляторе acc нету своего свойства с ключем tag,
// то создаем его и записывает ему значение 0.
// В противном случае увеличиваем значение на 1.
const getTagStats = (acc, tag) => {
  if (!acc.hasOwnProperty(tag)) {
    acc[tag] = 0;
  }

  acc[tag] += 1;

  return acc;
};

// Начальное значение аккумулятора это пустой объект {}
const countTags = tags => tags.reduce(getTagStats, {});

const tagCount = countTags(tags);
console.log(tagCount);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/reduce"
            target="_blank"
            rel="noopener noreferrer"
          >
            Array.reduce на MDN
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://hackernoon.com/reduce-your-fears-about-array-reduce-629b334ab945"
            target="_blank"
            rel="noopener noreferrer"
          >
            Reduce your fears about Array.reduce()
          </a>
        </li>
      </ul>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.7 - Array.prototype.sort()</h3>
      <p className={styles.description}>
        Позволяет сортировать элементы массива на месте. Помимо возврата отсортированного массива метод{' '}
        <code className={styles.code}>sort</code> также отсортирует массив, на котором он был вызван. По умолчанию метод{' '}
        <code className={styles.code}>sort</code> сортирует, преобразуя элементы к строке.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>Перебирает</li>
        <li className={styles.list__item}>Изменяет</li>
        <li className={styles.list__item}>Возвращает коллекцию</li>
      </ul>
      <CopyBlock
        text={`const numbers = [2, 1, 4, 3, 5];
// Отсортирует по возрастанию
console.log('Before sort: ', numbers); // [2, 1, 4, 3, 5]
console.log('After sort: ', numbers.sort()); // [1, 2, 3, 4, 5]

const clients = ['Mango', 'Ajax', 'Poly', 'Chelsey'];

// Отсортирует по алфавиту
console.log('Before sort: ', clients); // ["Mango", "Ajax", "Poly", "Chelsey"]
console.log('After sort: ', clients.sort()); // ["Ajax", "Chelsey", "Mango", "Poly"]`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.7.1 - Свой порядок сортировки</h4>
        <p className={styles.description}>
          Для указания своего порядка сортировки в метод <code className={styles.code}>arr.sort(fn)</code> нужно
          передать функцию <code className={styles.code}>fn</code> с двумя параметрами, которая сравнивает их.
          Внутренний алгоритм функции сортировки умеет сортировать любые массивы. Но для этого ему нужно знать, как их
          сравнивать. Эту роль и выполняет <code className={styles.code}>fn</code>.{' '}
        </p>
        <p className={styles.description}>
          Алгоритм сортировки, встроенный в JavaScript, будет передавать ей для сравнения элементы массива. Она должна
          возвращать:{' '}
        </p>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            Положительное значение, если <code className={styles.code}>a &gt; b</code>
          </li>
          <li className={styles.list__item}>
            Отрицательное значение, если <code className={styles.code}>a &lt; b</code>
          </li>
          <li className={styles.list__item}>
            Если равны – можно <code className={styles.code}>0</code>, но вообще не важно, что возвращать, если их
            взаимный порядок не имеет значения.
          </li>
        </ul>
        <p className={styles.description}>
          Отсортируем массив наших пользователей по возрастанию дней онлайн активности.{' '}
        </p>
        <CopyBlock
          text={`const users = [
  { name: 'Mango', daysActive: 15 },
  { name: 'Poly', daysActive: 4 },
  { name: 'Ajax', daysActive: 27 },
  { name: 'Chelsey', daysActive: 2 },
];

const sortByActiveDays = (a, b) => a.daysActive - b.daysActive;

console.log(users.sort(sortByActiveDays));`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          <a
            className={styles.link}
            href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort"
            target="_blank"
            rel="noopener noreferrer"
          >
            Array.sort на MDN
          </a>
        </p>
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.8 - Цепочки методов массива</h3>
      <p className={styles.description}>
        Есть массив чисел, из него необходимо взять все четные и умножить на 2, после чего еще и развернуть массив.
        Выразим кодом решение так как мы уже умеем это делать.{' '}
      </p>
      <CopyBlock
        text={`const numbers = [1, 2, 3, 4, 5, 6, 7, 8];

const even = numbers.filter(x => x % 2 === 0);

const doubled = even.map(x => x * 2);

const reversed = doubled.reverse();

console.log(reversed);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Вариант решения выше неплох. Проблема в том, что у нас появляются промежуточные переменные после каждой
        операции. Избавиться от них можно используя цепочки вызовов методов.{' '}
      </p>
      <p className={styles.description}>
        Функциональные методы массива можно группировать в цепочки. Каждый следующий метод будет выполняться на
        результате работы предыдущего.{' '}
      </p>
      <CopyBlock
        text={`const numbers = [1, 2, 3, 4, 5, 6, 7, 8];

/*
 * Фильтруем четные числа. Далее, на результате метода filter,
 * вызываем map и множим на 2. После чего на результате
 * метода map вызываем reverse.
 */

const result = numbers
  .filter(x => x % 2 === 0)
  .map(y => y * 2)
  .reverse();

console.log(result);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
  </section>
))
